import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Mail, FileText } from "lucide-react";
import Logo from "../components/Logo";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";
import { useAuth } from "../context/AuthContext";

export default function Confirmation() {
  const { bookingId } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((res) => setBooking(res.data));
  }, [bookingId]);

  if (!booking) return null;

  const dateStr = new Date(booking.experience_snapshot.date).toLocaleDateString(
    lang === "ca" ? "ca-ES" : lang === "en" ? "en-GB" : "es-ES",
    { day: "2-digit", month: "long", year: "numeric" },
  );

  return (
    <div className="px-6 pt-12 pb-20" data-testid="confirmation-page">
      <div className="text-center stagger">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-green/15 text-brand-green">
          <CheckCircle2 size={28} strokeWidth={1.4} />
        </div>
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan mt-5">
          ARISTEIA
        </p>
        <h1 className="font-serif text-4xl text-brand-brown mt-2 leading-[1.05]">
          {t("confirmed")}
        </h1>
        <p className="text-sm text-brand-brown/70 mt-3 max-w-[30ch] mx-auto">
          {t("confirmed_sub")}
        </p>
      </div>

      <div
        className="mt-9 rounded-3xl border-2 border-dashed border-brand-tan/40 bg-white/70 p-6"
        data-testid="ticket"
      >
        <div className="flex items-center justify-between">
          <Logo size="sm" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
            #{booking.id.slice(0, 8).toUpperCase()}
          </span>
        </div>

        <div className="mt-5">
          <p className="text-[10px] tracking-[0.35em] uppercase font-bold text-brand-tan">
            {t("chapter")}
          </p>
          <p className="font-serif text-3xl text-brand-brown leading-tight mt-1">
            {pick(booking.experience_snapshot.title, lang)}
          </p>
          <p className="text-xs tracking-wider uppercase text-brand-tan mt-2">
            {booking.experience_snapshot.location}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-brand-tan/30 pt-4">
          <Stat label={t("calendar")} value={dateStr} />
          <Stat label={t("guests")} value={booking.guests} />
          <Stat label={t("total")} value={`€${booking.total_eur}`} />
          <Stat
            label={t("invoice_ref")}
            value={booking.holded_mock?.invoice_ref || "—"}
          />
        </div>

        <div className="mt-6 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-brand-green">
          <Mail size={14} strokeWidth={1.5} />
          {t("confirmation_email_to")} {user?.email}
        </div>
        <div className="mt-2 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
          <FileText size={14} strokeWidth={1.5} />
          Holded · {booking.holded_mock?.synced ? "sync OK" : "pending"}
        </div>
      </div>

      <Link
        to="/trips"
        data-testid="confirmation-cta"
        className="mt-9 w-full inline-flex items-center justify-center py-4 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-sm transition-transform active:scale-[0.98]"
      >
        {t("view_trips")}
      </Link>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[9px] tracking-[0.3em] uppercase font-bold text-brand-tan">
        {label}
      </p>
      <p className="text-sm font-bold text-brand-brown mt-1 break-words">
        {value}
      </p>
    </div>
  );
}
